/*
 * Therapeutic Class Report - Drug Tab Subset View
 * 
 * This is a focused subset view of the therapeutic class report for use in the drug details tab.
 * It shows a limited view with the drug's classification pre-selected and only state/pool filtering.
 * 
 * Features:
 * - Classification and drug are pre-selected and locked (based on current drug)
 * - Only state/pool filtering available (simplified UX)
 * - Focused analysis for the specific drug within its therapeutic class
 * - Link to full report for comprehensive analysis
 * 
 * For the full report with all features, see: /reports/therapeutic-class-report
 */

import { ProtectedRoute } from '@shared/auth';
import { useMemo, useState } from 'react';
import { HiDownload } from 'react-icons/hi';
import { HiChevronRight } from 'react-icons/hi2';

import { useDrugsInTherapeuticClass } from '@/api/use-drugs-in-therapeutic-class';
import { type InfiniteTherapeuticClassReportParams, useInfiniteTherapeuticClassReport } from '@/api/use-therapeutic-class-report-infinite';
import { type TherapeuticClassReportSummaryParams, useTherapeuticClassReportSummary } from '@/api/use-therapeutic-class-report-summary';
import { Button } from '@/components-new/button';
import { SectionDescription, SectionHeading } from '@/components-new/section';
import { useDrug } from '@/features/drugs/api/use-drug';
import { 
  TherapeuticClassFilters, 
  useAutoInitializeFilters,
  useTherapeuticClassExport,
  useTherapeuticClassReportParams } from '@/features/therapeutic-class';
import { WindowedTherapeuticClassTable } from '@/features/therapeutic-class/components/windowed-therapeutic-class-table';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useParams } from '@/router';
import { drugName } from '@/utils/drugs';

const DrugTherapeuticClassReportPage = () => {
  // Get drug from URL params (drug tab context)
  const { id } = useParams('/drugs/:id');
  const { data: drug } = useDrug(id);
  const { hasPolicies } = useCurrentUser();
  
  // Check if user is internal (Artia staff) or external (client user)
  const isInternalUser = hasPolicies(['isAnyArtiaUser']);
  
  // Get client-filtered drugs in the therapeutic class (for external users only)
  const { data: drugsInClass = [] } = useDrugsInTherapeuticClass(drug?.classification?.id);
  
  const drugDisplayName = drug ? drugName(drug) : '';

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'Ascending' | 'Descending'>('Ascending');

  // Handle sorting changes
  const handleSortChange = (columnId: string, direction: 'Ascending' | 'Descending') => {
    setSortBy(columnId);
    setSortDirection(direction);
  };

  // Extract drug IDs for report filtering
  // Internal users: no drug filtering (empty array means all drugs)
  // External users: only client-tracked drugs
  const reportDrugIds = useMemo(() => {
    if (isInternalUser) {
      // Internal users see all drugs in the therapeutic class
      return [];
    } else {
      // External users see only client-tracked drugs
      return drugsInClass.map(drug => drug.id);
    }
  }, [isInternalUser, drugsInClass]);

  // Set up report parameters with forced enable since we have the drug context
  const { 
    reportParams, 
    enableApiCall, 
    therapeuticClassFilter 
  } = useTherapeuticClassReportParams({
    classificationId: drug?.classification?.id,
    drugIds: reportDrugIds, // Use conditional drug filtering based on user type
    forceEnabled: !!drug?.classification,
  });  // Auto-initialize filters
  useAutoInitializeFilters({ therapeuticClassFilter });

  // Prepare infinite report parameters
  const infiniteReportParams: InfiniteTherapeuticClassReportParams | null = useMemo(() => {
    if (!reportParams) return null;
    
    return {
      classificationId: reportParams.classificationId,
      drugIds: reportParams.drugIds,
      stateIds: reportParams.stateIds,
      poolIds: reportParams.poolIds,
      paTypeIds: reportParams.paTypeIds,
      pdlStatuses: reportParams.pdlStatuses,
      pageSize: 250, // Enhanced page size for better performance
      sortBy: sortBy || undefined,
      sortDirection: sortBy ? sortDirection : undefined
    };
  }, [reportParams, sortBy, sortDirection]);

  // Prepare summary parameters (same as infinite params but without pageSize)
  const summaryParams: TherapeuticClassReportSummaryParams | null = useMemo(() => {
    if (!reportParams) return null;
    
    return {
      classificationId: reportParams.classificationId,
      drugIds: reportParams.drugIds,
      stateIds: reportParams.stateIds,
      poolIds: reportParams.poolIds,
      paTypeIds: reportParams.paTypeIds,
      pdlStatuses: reportParams.pdlStatuses,
    };
  }, [reportParams]);

  // Fetch summary data separately
  const { 
    data: summaryData, 
    isLoading: isLoadingSummary 
  } = useTherapeuticClassReportSummary(
    summaryParams,
    enableApiCall && !!summaryParams && !!drug?.classification
  );

  // Fetch therapeutic class report data with infinite scrolling
  const { 
    data: reportData, 
    isLoading: isLoadingReport,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteTherapeuticClassReport(
    infiniteReportParams!,
    enableApiCall && !!infiniteReportParams && !!drug?.classification
  );

  // Use report data or fallback to empty array
  const tableData = reportData || [];

  // Combine loading states
  const isLoading = isLoadingReport || isLoadingSummary;

  // Prepare summary metrics for display
  const summaryMetrics = useMemo(() => {
    if (summaryData) {
      return {
        totalDrugs: summaryData.numberOfDrugsAnalyzed || 0,
        totalStates: summaryData.numberOfStatesAnalyzed || 0,
        mostPreferred: summaryData.mostPreferredDrug || '-',
        mostCommonPA: summaryData.mostCommonPaType || 'N/A'
      };
    }
    
    return {
      totalDrugs: 0,
      totalStates: 0,
      mostPreferred: '-',
      mostCommonPA: 'N/A'
    };
  }, [summaryData]);

  // Prepare export parameters for server-side export
  const exportParams = useMemo(() => {
    if (!reportParams) return undefined;
    
    return {
      classificationId: reportParams.classificationId,
      drugIds: reportParams.drugIds,
      stateIds: reportParams.stateIds,
      poolIds: reportParams.poolIds,
      paTypeIds: reportParams.paTypeIds,
      pdlStatuses: reportParams.pdlStatuses,
    };
  }, [reportParams]);

  // Export functionality - server-side only
  const { handleExport, canExport } = useTherapeuticClassExport({
    exportParams,
  });

  // Show classification requirement message if drug has no classification
  if (drug && !drug.classification) {
    return (
      <div className="mt-4">
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <svg
            className="mx-auto size-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-semibold text-gray-900">
            No therapeutic classification
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            This drug does not have a therapeutic classification. Please contact your administrator to assign a therapeutic class to this drug.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <div>
          <SectionHeading level={3}>Therapeutic Class Report</SectionHeading>
          <SectionDescription>
            Coverage analysis for {drugDisplayName} within the {drug?.classification?.name} therapeutic class.
          </SectionDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button outline href="/market-reports/therapeutic-class-report">
            <HiChevronRight/>
            Full Report
          </Button>
          <Button outline onClick={handleExport} disabled={!canExport}>
            <HiDownload/>
            Export
          </Button>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="mt-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="flex size-8 items-center justify-center rounded-md bg-slate-500 text-white">
                  <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Drugs Analyzed</div>
                <div className="text-2xl font-bold text-gray-900">
                  {isLoadingSummary ? '...' : summaryMetrics.totalDrugs}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="flex size-8 items-center justify-center rounded-md bg-emerald-600 text-white">
                  <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">States Analyzed</div>
                <div className="text-2xl font-bold text-gray-900">
                  {isLoadingSummary ? '...' : summaryMetrics.totalStates}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="flex size-8 items-center justify-center rounded-md bg-indigo-600 text-white">
                  <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Most Preferred Drug</div>
                <div className="truncate text-lg font-semibold text-gray-900" title={summaryMetrics.mostPreferred}>
                  {isLoadingSummary ? '...' : summaryMetrics.mostPreferred}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="flex size-8 items-center justify-center rounded-md bg-amber-600 text-white">
                  <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Most Common PA Type</div>
                <div className="truncate text-lg font-semibold text-gray-900" title={summaryMetrics.mostCommonPA}>
                  {isLoadingSummary ? '...' : summaryMetrics.mostCommonPA}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Simplified Filters - Only States/Pools */}
      <div className="mt-6">
        <TherapeuticClassFilters filter={therapeuticClassFilter}/>
      </div>
      
      <div className="mt-6">
        <WindowedTherapeuticClassTable 
          data={tableData}
          showDrugColumn={true}
          showStepTherapyColumn={isInternalUser}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
        />
      </div>
    </div>
  );
};

const DrugTherapeuticClassReportPageContainer = () => {
  return (
    <ProtectedRoute>
      <DrugTherapeuticClassReportPage/>
    </ProtectedRoute>
  );
};

export default DrugTherapeuticClassReportPageContainer;
